import { Grid, TextField } from '@mui/material';
import { SelectField } from 'components';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { createFilter } from 'react-select';
import { ApplicationRolesService, UserService } from 'services';
import { translate } from 'utils';
import { APP_NAMES_FRONT } from 'utils/constants';

export const UserListFilters = ({
  currentFilters, setCurrentFilters
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const location = useLocation();
  const originApp = location.search && location.search.split('=')[1].toUpperCase();

  const [appNames, setAppNames] = useState([]);
  const [appRoles, setAppRoles] = useState([]);

  const originAppForRole = originApp !== undefined ? originApp : APP_NAMES_FRONT.DATATECNEA;

  const companyName = (currentFilters.find(filt => filt.key === 'companyName')
    && currentFilters.find(filt => filt.key === 'companyName').label) || '';
  const applications = (currentFilters.find(filt => filt.key === 'applications')
    && currentFilters.find(filt => filt.key === 'applications').value) || null;

  const roles = (currentFilters.find(filt => filt.key === 'roles')
    && currentFilters.find(filt => filt.key === 'roles').value) || null;

  const siren = (currentFilters.find(filt => filt.key === 'siren')?.label) || '';
  const siret = (currentFilters.find(filt => filt.key === 'siret')?.label) || '';
  const institutionName = (currentFilters.find(filt => filt.key === 'institutionName')?.label) || '';
  const postalCode = (currentFilters.find(filt => filt.key === 'postalCode')?.label) || '';
  const city = (currentFilters.find(filt => filt.key === 'city')?.label) || '';
  const activeValue = currentFilters.find(filt => filt.key === 'active')?.value;
  const active = activeValue === true
    ? { value: true, label: translate('common.withUserAccount') }
    : activeValue === false
      ? { value: false, label: translate('common.withoutUserAccount') }
      : null;


  const getRolesForApp = useCallback(appName => {
    ApplicationRolesService.getOptions(appName)
      .then(response => setAppRoles(response))
      .catch(error => enqueueSnackbar((error && error.message) || error, { variant: 'error' }));
  }, [enqueueSnackbar]);

  const getRolesForApps = useCallback(appNames => {
    if (!appNames || appNames.length === 0) {
      setAppRoles([]);
      return;
    }
    // Si c’est une seule app, on transforme en tableau
    const apps = Array.isArray(appNames) ? appNames : [appNames];

    // Appeler l’API pour chaque application et fusionner les résultats
    Promise.all(apps.map(app => ApplicationRolesService.getOptions(app)))
      .then(results => {
        // Fusionner tous les rôles en évitant les doublons
        const mergedRoles = results.flat().reduce((acc, role) => {
          if (!acc.some(r => r.value === role.value)) acc.push(role);
          return acc;
        }, []);
        setAppRoles(mergedRoles);
      })
      .catch(error => enqueueSnackbar((error && error.message) || error, { variant: 'error' }));
 }, [enqueueSnackbar]);

  const getApplicationNames = useCallback(() => {
    UserService.getAppNamesOptions()
      .then(response => {
        const filteredApps = response.filter(app => !['DATACOMMONS', 'DATAHYDROTEST'].includes(app.value));
        setAppNames(filteredApps);

        if (!applications || applications.length === 0) {
          const allAppNames = filteredApps.map(app => app.value);
          getRolesForApps(allAppNames);
        }
      })
      .catch(error => enqueueSnackbar((error && error.message) || error, { variant: 'error' }));
  }, [enqueueSnackbar, applications, getRolesForApps]);

  const handleChangeInput = useCallback(event => {
    const { name: filterName, value } = event.target;

    if (value) {
      setCurrentFilters([...currentFilters.filter(filt => filt.key !== filterName), { key: filterName, label: value }]);
    } else {
      setCurrentFilters([...currentFilters.filter(filt => filt.key !== filterName)]);
    }
  }, [currentFilters, setCurrentFilters]);

  const handleSelect = useCallback((value, filterName) => {
    if (value) {

      let storedValue = value;

      if (filterName === 'active') {
        storedValue = value.value;
      }

      setCurrentFilters([...currentFilters.filter(filt => filt.key !== filterName), {
        key: filterName,
        label: Array.isArray(value) ? value.map(v => v.label).join(', ') : value.label || value,
        value: storedValue
      }]);
      if (filterName === 'applications') {
        const appNames = Array.isArray(value) ? value.map(v => v.value) : [value.value];
        getRolesForApps(appNames);
      }
    } else {
      setCurrentFilters([...currentFilters.filter(filt => filt.key !== filterName)]);
      getRolesForApps([]);
    }
  }, [currentFilters, setCurrentFilters, getRolesForApps]);

  const activeOptions = [
    { value: true, label: translate('common.withUserAccount') },
    { value: false, label: translate('common.withoutUserAccount') },
  ];

  useEffect(() => {
    getApplicationNames();
    getRolesForApp(originAppForRole);
    // eslint-disable-next-line
  }, [originAppForRole]);

  return (
    <Grid container direction="column" spacing={2}>
      <Grid item xs={12}>
        <TextField
          autoComplete="off"
          label={translate('common.userCompany')}
          name="companyName"
          value={companyName}
          variant="outlined"
          onChange={handleChangeInput}
        />
      </Grid>
      <Grid item xs={12}>
        <SelectField
          closeMenuOnSelect={false}
          filterOption={createFilter({ stringify: option => `${option.label}` })}
          isMulti
          label="common.applications"
          name="applications"
          options={appNames}
          value={applications}
          onChange={value => handleSelect(value, 'applications')}
        />
      </Grid>
      <Grid item xs={12}>
        <SelectField
          closeMenuOnSelect={false}
          filterOption={createFilter({ stringify: option => `${option.label}` })}
          isMulti
          label="common.roles"
          name="roles"
          options={appRoles}
          value={roles}
          onChange={value => handleSelect(value, 'roles')}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          autoComplete="off"
          label={translate('common.siren')}
          name="siren"
          value={siren}
          variant="outlined"
          onChange={handleChangeInput}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          autoComplete="off"
          label={translate('common.siret')}
          name="siret"
          value={siret}
          variant="outlined"
          onChange={handleChangeInput}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          autoComplete="off"
          label={translate('common.institutionName')}
          name="institutionName"
          value={institutionName}
          variant="outlined"
          onChange={handleChangeInput}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          autoComplete="off"
          label={translate('common.postalCode')}
          name="postalCode"
          inputProps={{ maxLength: 5}}
          value={postalCode}
          variant="outlined"
          onChange={e => {
            // Ne garde que les chiffres et limite à 5 caractères
            const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 5);
            handleChangeInput({ target: { name: 'postalCode', value: onlyDigits } });
          }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          autoComplete="off"
          label={translate('common.city')}
          name="city"
          value={city}
          variant="outlined"
          onChange={handleChangeInput}
        />
      </Grid>
      <Grid item xs={12}>
        <SelectField
          closeMenuOnSelect={false}
          filterOption={createFilter({ stringify: option => `${option.label}` })}
          label="common.userAccount"
          name="active"
          options={activeOptions}
          value={active}
          onChange={value => handleSelect(value, 'active')}
        />
      </Grid>
    </Grid>
  );
};

UserListFilters.propTypes = {
  currentFilters: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setCurrentFilters: PropTypes.func.isRequired
};
